import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {TableDataBuilder} from "../Table";
import TableContainer from "@material-ui/core/TableContainer";
import {Box, TableCell, TableHead, Typography} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@material-ui/core/Checkbox";
import TableBody from "@material-ui/core/TableBody";
import UserEvent from "../../core/UserEvent";
import {makeStyles} from "@material-ui/core/styles";
import lang from "../../core/lang";
import Sort from "../../core/Sort";
import clsx from "clsx";
import Badge from "@material-ui/core/Badge";
import Icon from "../Icon";
import code from "../../core/code";
import i18n from "../../core/i18n";

const SelectionMode = {
    NONE: 'none',
    SINGLE: 'single',
    SIMPLE: 'simple',
    MULTI: 'multi'
};

const useStyles = makeStyles((theme) => ({
    root: {},
    selectable: {
        cursor: 'pointer'
    },
    sortable: {
        cursor: 'pointer'
    },
    headRowCell: {
        // backgroundColor: theme.palette.background.paper
    },
    bodyRowCell: {
        height: props => props.rowHeight
    }
}));

const computeRowHeight = (dense) => {
    const fontSize = parseInt(window.getComputedStyle(document.body).getPropertyValue('font-size'));
    const lineHeight = 1.5;
    const paddingY = (dense ? 8 : 16) * 2;
    const borderSizeY = 1;
    const rowHeight = fontSize * lineHeight + paddingY + borderSizeY;
    return rowHeight;
}

function DataTable(props) {
    const { data, pageNumber  = 0, pageSize = 0, children, rowId, selectionMode,
        stickyHeader, maxHeight, checkbox, numbering, dense, size, rowHeight = computeRowHeight(dense)} = props;
    const sort = lang.isNullOrUndefined(props.sort) ? new Sort() : Sort.of(props.sort);
    const classes = useStyles({rowHeight});
    const selection = props.selection || [];
    const style = { };
    const count = data.length;
    const columns = [];
    const gap = props.gap;
    const gapX = props.gapX;
    const gapY = props.gapY || ( dense ? 1 : undefined);

    React.Children.forEach(children, child => {
        if(child?.type===DataColumn && !child.props.hidden) {
            columns.push(child);
        }
    });
    const [codeData, setCodeData] = useState({});

    useEffect(() => {
        (async () => {
            const codeData={};
            let type;
            for (const column of columns) {
                type = column.props.code;
                if (type) {
                    codeData[type] = await code.fetch(type);
                }
            }
            setCodeData(codeData);
        })()
    }, [])

    const handleDataRowClick = (rowId, rowIndex) => event => {
        if(selectionMode!==SelectionMode.NONE) {
            const ctrlKey = event.ctrlKey || event.metaKey;
            const isSelected = tableData.isSelected(rowId);
            if(lang.isBoolean(event.target.checked) || ctrlKey) {
                // click on the checkbox or hold the ctrlKey to avoid the simple selection mode.
                if(isSelected) {
                    tableData.deselect(rowId);
                } else {
                    if(selectionMode===SelectionMode.SINGLE) {
                        tableData.clearSelection();
                    }
                    tableData.select(rowId);
                }
            } else {
                if(isSelected) {
                    tableData.deselect(rowId);
                } else {
                    if(selectionMode===SelectionMode.SINGLE||selectionMode===SelectionMode.SIMPLE) {
                        tableData.clearSelection();
                    }
                    tableData.select(rowId);
                }
            }
            const selection = tableData.getSelection();

            const selectEvent = new UserEvent({selection}, event);
            (selectionMode!==SelectionMode.NONE) && props.onSelect && props.onSelect(selectEvent);
        }

        const clickEvent = new UserEvent({rowId, row: rowIndex}, event);
        props.onRowClick && props.onRowClick(clickEvent);
    }

    const handleDataCellClick = (cell, rowId, rowIndex, colIndex) => event => {
        const clickEvent = new UserEvent({...cell, rowId, row:rowIndex, col: colIndex}, event);
        props.onCellClick && props.onCellClick(clickEvent);
    }

    const handleSelectPageClick = (event) => {
        const checked = event.currentTarget.checked;
        const selection = checked ? tableData.selectAll() : tableData.deselectAll();
        props.onSelect && props.onSelect(new UserEvent({selection}, event));
    }

    const handleHeadRowCellClick = (index) => (event) => {
        const column = columns[index];
        if(!column.props.sortable || !lang.isString(column.props.accessor)) return;
        const property = column.props.accessor;
        const order = sort.find(property);
        let newSort;
        if(lang.isNullOrUndefined(order)) {
            newSort = sort.and(Sort.asc(property));
        } else {
            newSort = sort.filter(order => order.property!==property);
            if(order.direction===Sort.Direction.ASC) {
                newSort = newSort.and(Sort.desc(property));
            }
        }
        props.onSort && props.onSort(new UserEvent({sort: newSort}, event))
    }

    const computedMaxHeight = (maxHeight) => {
        if(!maxHeight?.endsWith('row')) {
            return maxHeight;
        }

        const headRowHeight = computeRowHeight();

        return rowHeight * parseInt(maxHeight) + headRowHeight;
    }

    style.maxHeight = computedMaxHeight(maxHeight);

    const builder = TableDataBuilder.newTableData(data)
        .withRowId(rowId)
        .withSelection(selection);
    columns.forEach(column => {
        const accessor = column.props.accessor;
        const label = column.props.label;
        builder.addColumn(accessor, label, {...column.props})
    });
    const tableData = builder.build();

    let offset, limit;
    if(pageSize>0) {
        const page = Math.min(pageNumber, Math.ceil(count/pageSize) - 1);
        offset = page * pageSize;
        limit = pageSize;
    } else {
        offset = 0;
        limit = count;
    }
    // dataRows is the rows in current page
    const dataRows = tableData.getDataRows(offset, limit);
    let height, defaultAligns = new Array(columns.length);
    defaultAligns.fill(undefined);
    for(let row=0; row<dataRows.length; row++) {
        const cells = dataRows[row].getCells();
        cells.forEach((cell, col) => {
            const { value, accessor, renderer } = cell;
            let align;
            if(renderer || lang.isFunction(accessor)) {
                align = 'center';
            } else if(lang.isNotNullOrUndefined(value)) {
                if(lang.isNumber(value)) {
                    align = 'right';
                } else if(lang.isBoolean(value)) {
                    align = 'center';
                } else if(lang.isDate(value)) {
                    align = 'center';
                } else {
                    align = 'left';
                }
            }

            lang.isUndefined(align) || (defaultAligns[col] = align);
        });
        if(defaultAligns.every(align => lang.isNotUndefined(align))) {
            break;
        }
    }
    defaultAligns = defaultAligns.map(align => align || 'left');
    const tableBodyRows = dataRows.map((row, index) => {
        const rowIndex = pageNumber * pageSize + index;
        const { rowId } = row;
        const variant = (size === 'small') ? 'body2' : 'body1';
        const isRowSelected = selection.includes(rowId);
        const tableBodyRowCells = row.getCells().map((cell, index) => {
            const { content, value, accessor, renderer, maxWidth, minWidth, width, zero, empty, code, whiteSpace, checkbox } = cell;
            const fontFamily = lang.isFunction(cell.fontFamily) ? cell.fontFamily(value, {value, accessor, rowId, row: row.data, rowIndex, colIndex: index}) : cell.fontFamily;
            const defaultAlign = defaultAligns[index];
            const noWrap = !!maxHeight?.endsWith('row');
            let element, align;
            if(checkbox) {
                const disabled = (selectionMode===SelectionMode.NONE);
                return <TableCheckboxCell key={index} padding={gap} paddingX={gapX} paddingY={gapY} checked={isRowSelected} disabled={disabled} />;
            }
            if(renderer || lang.isFunction(accessor)) {
                element = lang.isString(content) ? <Typography variant={variant} className={classes.content} noWrap={noWrap} color="textPrimary">{content}</Typography> : content;
                align = cell.align || defaultAlign;
            } else if(code && (lang.isString(value) || (lang.isArray(value) && value.every(item => lang.isString(item))))) {
                align = cell.align || defaultAlign;
                const options = codeData[code] || [];
                let text;
                if (lang.isString(value)) {
                    text = options.find(option => option.value === value)?.text || value;
                }
                else {
                    text = value.map(item => options.find(option => option.value === item)?.text || value).join(', ');
                }
                element = <Typography variant={variant} noWrap={noWrap} color="textPrimary">{text}</Typography>;
            } else {
                const style = {whiteSpace, fontFamily};
                if(lang.isNullOrUndefined(value)) {
                    align = cell.align || defaultAlign;
                    element = <Typography variant={variant} noWrap={noWrap} style={style}>{lang.isNotNullOrUndefined(empty) ? empty : ''}</Typography>;
                } else if(lang.isNumber(value)) {
                    align = cell.align || defaultAlign;
                    element = <Typography variant={variant}  noWrap={noWrap} color="textPrimary" style={style}>{(lang.isNotNullOrUndefined(zero) && value===0) ? zero : content}</Typography>;
                } else if(lang.isBoolean(value)) {
                    align = cell.align || defaultAlign;
                    element = value ? <Icon size="1rem">check</Icon> : <Icon size="1rem">clear</Icon>
                } else if(lang.isDate(value)) {
                    align = cell.align || defaultAlign;
                    element = <Typography variant={variant} noWrap={noWrap} color="textPrimary" style={style}>{content}</Typography>;
                } else {
                    align = cell.align || defaultAlign;
                    element = <Typography variant={variant} noWrap={noWrap} color="textPrimary" style={style}>{content}</Typography>;
                }
            }
            const style = {height, width, maxWidth, minWidth};

            return (
                <TableCell className={classes.bodyRowCell} key={index} align={align} style={style}
                           onClick={handleDataCellClick(cell, rowId, rowIndex, index)}
                           padding="none">
                    <Box padding={gap} paddingX={gapX} paddingY={gapY}>
                        {element}
                    </Box>
                </TableCell>
            )
        });
        let numberingCell, checkboxCell;
        if(numbering) {
            numberingCell = (
                <TableCell key={-2} padding="none">
                    <Box padding={gap} paddingX={gapX} paddingY={gapY}>
                        <Typography variant={variant}>{rowIndex+1}</Typography>
                    </Box>
                </TableCell>
            );
        }
        if(checkbox) {
            const disabled = (selectionMode===SelectionMode.NONE);
            checkboxCell =  <TableCheckboxCell key={-1} padding={gap} paddingX={gapX} paddingY={gapY} checked={isRowSelected} disabled={disabled} />;
        }

        return (
            <TableRow className={classes.selectable} key={rowId} selected={isRowSelected}
                      onClick={handleDataRowClick(rowId, rowIndex)} hover>
                {checkboxCell}
                {numberingCell}
                {tableBodyRowCells}
            </TableRow>
        );
    });

    const tableHeadRowCells = columns.map((column, index) => {
        const { label, sortable, accessor, minWidth, maxWidth, width, checkbox, headRenderer } = column.props;
        const align = column.props.align || defaultAligns[index];
        const variant = (size==='small') ? 'body2' : 'body1';
        const className = clsx(classes.headRowCell, sortable && classes.sortable, "appfuse-datatable-header-row-cell");
        let sortIndicator = null;

        if(checkbox) {
            const disabled = (selectionMode===SelectionMode.NONE||selectionMode===SelectionMode.SINGLE);
            const size = dataRows.length;
            // selection in the current page
            const pageSelection = dataRows.filter(dataRow => selection.includes(dataRow.rowId));
            return (
                <TableCheckboxCell key={index} className={classes.headRowCell} padding={gap} paddingX={gapX} paddingY={gapY}
                           disabled={disabled}
                           indeterminate={pageSelection.length > 0 && pageSelection.length < size}
                           checked={size > 0 && pageSelection.length === size}
                           onChange={handleSelectPageClick} />
            );
        }

        if(sortable) {
            const index = sort.indexOf(accessor);
            if(index>=0) {
                const order = sort.orders[index];
                const sortIcon = (order.direction===Sort.Direction.ASC) ?  "arrow_drop_up" : "arrow_drop_down";
                const sortPriority = index + 1;
                if(sort.orders.length>1) {
                    const anchorOrigin = { vertical: 'top' };
                    anchorOrigin.horizontal = (align==='right') ? 'left' : 'right';
                    sortIndicator = (
                        <Badge anchorOrigin={anchorOrigin} badgeContent={sortPriority}>
                            <Icon>{sortIcon}</Icon>
                        </Badge>
                    );
                } else {
                    sortIndicator = <Icon>{sortIcon}</Icon>;
                }
            }
        }
        const justifyContent = (function(align) {
            if(align==='right') {
                return 'flex-end';
            } else if(align==='center') {
                return 'center';
            } else {
                return 'flex-start';
            }
        }(align));

        // 預設是不自動換行，因為換行會讓版面看起來不美觀。但是如果有限定欄寬，那就設定成自動自動換行。
        const noWrap = lang.isUndefined(width, maxWidth);
        const content = (
                <Box display="flex" justifyContent={justifyContent} alignItems='center' padding={gap} paddingX={gapX} paddingY={gapY}>
                    {align==='right' && sortIndicator}
                    <Typography variant={variant} color="inherit" noWrap={noWrap}>{i18n.translate(label)}</Typography>
                    {align!=='right' && sortIndicator}
                </Box>
            )
        const element = headRenderer ? headRenderer(content, {...column.props, sort}) : content;

        const style = { minWidth, maxWidth, width}
        return (
            <TableCell className={className} style={style} key={index} align={align} onClick={handleHeadRowCellClick(index)}
                       padding="none">
                <Box color="textSecondary">
                    {element}
                </Box>
            </TableCell>
        )
    });

    let headRowCheckboxCell, headRowNumberingCell;
    if(numbering) {
        headRowNumberingCell = (
            <TableCell key={-2} className={classes.headRowCell} padding="none" ><Box padding={gap} paddingX={gapX} paddingY={gapY}/></TableCell>
        );
    }
    if(checkbox) {
        const disabled = (selectionMode===SelectionMode.NONE||selectionMode===SelectionMode.SINGLE);
        const size = dataRows.length;
        // selection in the current page
        const pageSelection = dataRows.filter(dataRow => selection.includes(dataRow.rowId));

        headRowCheckboxCell = (
            <TableCheckboxCell key={-1} className={classes.headRowCell} padding={gap} paddingX={gapX} paddingY={gapY}
                               disabled={disabled}
                               indeterminate={pageSelection.length > 0 && pageSelection.length < size}
                               checked={size > 0 && pageSelection.length === size}
                               onChange={handleSelectPageClick} />
        )
    }

    return (
        <TableContainer className={clsx('appfuse-datatable', classes.root)} style={style}>
            <Table stickyHeader={stickyHeader} size={dense ? 'small' : 'medium'} >
                <TableHead>
                    <TableRow>
                        {headRowCheckboxCell}
                        {headRowNumberingCell}
                        {tableHeadRowCells}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function TableCheckboxCell({className, padding, paddingX, paddingY, checked, indeterminate, disabled, onChange}) {
    return (
        <TableCell className={className}>
            <Box padding={padding} paddingX={paddingX} paddingY={paddingY}>
                <Checkbox checked={checked} indeterminate={indeterminate} disabled={disabled} onChange={onChange}
                inputProps={{"aria-label": i18n.translate("Tap to Select")}}/>
            </Box>
        </TableCell>
    )
}

const DataColumn = () => {}

DataTable.propTypes = {
    data: PropTypes.array,
    pageNumber: PropTypes.number,
    pageSize: PropTypes.number,
    rowId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
    selection: PropTypes.array,
    onSelect: PropTypes.func,
    sort: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.instanceOf(Sort)
    ]),
    onSort: PropTypes.func,
    /**
     * number: # of pixel
     * string:
     * - html 的任何高度表示法。
     * - 多少列高：例如 '10row' 代表表格的最大高度是 10 列高。
     */
    maxHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    stickyHeader: PropTypes.bool,
    dense: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
    checkbox: PropTypes.bool,
    numbering: PropTypes.bool,
    rowHeight: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    selectionMode: PropTypes.oneOf(['simple', 'none', 'single', 'multi']),
    onCellClick: PropTypes.func
}

DataTable.defaultProps = {
    data: [],
    pageNumber: 0,
    pageSize: 0,
    selection: [],
    sort: new Sort(),
    stickyHeader: false,
    selectionMode: SelectionMode.SIMPLE,
    gap: 1.5,
    size: 'medium'
};

DataColumn.propTypes = {
    accessor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
    headRenderer: PropTypes.func,
    renderer: PropTypes.func,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
    formatter: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func
    ]),
    pattern: PropTypes.string,
    empty: PropTypes.string,
    zero: PropTypes.string,
    sortable: PropTypes.bool,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    maxWidth: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    minWidth: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    hidden: PropTypes.bool,
    code: PropTypes.string,
    gap: PropTypes.number,
    gapX: PropTypes.number,
    gapY: PropTypes.number,
    whiteSpace: PropTypes.string,
    fontFamily: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    checkbox: PropTypes.bool
};

DataColumn.defaultProps = {
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit'
}

export default DataTable;

export { DataColumn };